import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { 
  Plus, Ticket, Trash2, Edit2, Check, X, Percent, Calendar, 
  Users, DollarSign, TrendingUp, CreditCard, Eye, EyeOff,
  ChevronDown, ChevronUp, Phone, Mail, Banknote
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDiscountCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [expandedCode, setExpandedCode] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentData, setPaymentData] = useState({ codeId: null, month: "", amount: 0, notes: "" });
  
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discount_type: "percentage",
    discount_value: "",
    min_order: "30",
    max_uses: "",
    valid_from: "",
    valid_until: "",
    active: true,
    // Dati affiliato
    affiliate_name: "",
    affiliate_email: "",
    affiliate_phone: "",
    commission_percentage: "10"
  });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(`${API}/admin/discount-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(response.data);
    } catch (error) {
      console.error("Errore caricamento codici:", error);
      toast.error("Errore nel caricamento dei codici");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("admin_token");
    
    try {
      const data = {
        ...formData,
        code: formData.code.toUpperCase(),
        discount_value: formData.discount_type === "free_shipping" ? 0 : parseFloat(formData.discount_value),
        min_order: parseFloat(formData.min_order) || 0,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        commission_percentage: formData.commission_percentage !== "" ? parseFloat(formData.commission_percentage) : 0
      };

      if (editingCode) {
        await axios.put(`${API}/admin/discount-codes/${editingCode.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Codice aggiornato!");
      } else {
        await axios.post(`${API}/admin/discount-codes`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Codice creato!");
      }
      
      setShowForm(false);
      setEditingCode(null);
      resetForm();
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo codice sconto?")) return;
    
    const token = localStorage.getItem("admin_token");
    try {
      await axios.delete(`${API}/admin/discount-codes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Codice eliminato!");
      fetchCodes();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleMarkPaid = async () => {
    if (!paymentData.month || paymentData.amount <= 0) {
      toast.error("Inserisci mese e importo");
      return;
    }
    
    const token = localStorage.getItem("admin_token");
    try {
      await axios.post(`${API}/admin/discount-codes/${paymentData.codeId}/mark-paid`, {
        month: paymentData.month,
        amount: paymentData.amount,
        notes: paymentData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Pagamento registrato!");
      setShowPayModal(false);
      setPaymentData({ codeId: null, month: "", amount: 0, notes: "" });
      fetchCodes();
    } catch (error) {
      toast.error("Errore nella registrazione del pagamento");
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      discount_type: "percentage",
      discount_value: "",
      min_order: "30",
      max_uses: "",
      valid_from: "",
      valid_until: "",
      active: true,
      affiliate_name: "",
      affiliate_email: "",
      affiliate_phone: "",
      commission_percentage: "10"
    });
  };

  const startEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description || "",
      discount_type: code.discount_type || "percentage",
      discount_value: code.discount_value?.toString() || "",
      min_order: code.min_order?.toString() || "0",
      max_uses: code.max_uses?.toString() || "",
      valid_from: code.valid_from || "",
      valid_until: code.valid_until || "",
      active: code.active !== false,
      affiliate_name: code.affiliate_name || "",
      affiliate_email: code.affiliate_email || "",
      affiliate_phone: code.affiliate_phone || "",
      commission_percentage: code.commission_percentage?.toString() || "10"
    });
    setShowForm(true);
  };

  const openPayModal = (code, month, amount) => {
    setPaymentData({
      codeId: code.id,
      codeName: code.code,
      affiliateName: code.affiliate_name,
      month: month,
      amount: amount,
      notes: ""
    });
    setShowPayModal(true);
  };

  const handleUnmarkPaid = async (code, month) => {
    if (!window.confirm(`Annullare il pagamento di ${new Date(month + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" })}?`)) return;
    const token = localStorage.getItem("admin_token");
    try {
      await axios.post(`${API}/admin/discount-codes/${code.id}/unmark-paid`, { month }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Pagamento annullato");
      fetchCodes();
    } catch (error) {
      toast.error("Errore nell'annullamento");
    }
  };

  // Calcola totali
  const totalUnpaid = codes.reduce((sum, c) => sum + (c.stats?.unpaid_commission || 0), 0);
  const totalCommissions = codes.reduce((sum, c) => sum + (c.stats?.total_commission || 0), 0);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="discount-codes-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Ticket className="w-8 h-8 text-green-500" />
              Codici Sconto & Affiliati
            </h1>
            <p className="text-zinc-400 mt-1">Gestisci codici sconto e traccia commissioni affiliati</p>
          </div>
          <button
            onClick={() => { setShowForm(true); setEditingCode(null); resetForm(); }}
            className="bg-green-500 text-black px-6 py-3 rounded-lg font-bold flex items-center gap-2 hover:bg-green-600 transition-all"
          >
            <Plus className="w-5 h-5" />
            Nuovo Codice
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Ticket className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-zinc-400 text-sm">Codici Attivi</p>
                <p className="text-2xl font-bold text-white">{codes.filter(c => c.active).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-zinc-400 text-sm">Commissioni Totali</p>
                <p className="text-2xl font-bold text-green-500">€{totalCommissions.toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Banknote className="w-8 h-8 text-yellow-500" />
              <div>
                <p className="text-zinc-400 text-sm">Da Pagare</p>
                <p className="text-2xl font-bold text-yellow-500">€{totalUnpaid.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Codes List */}
        <div className="space-y-4">
          {codes.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-12 text-center">
              <Ticket className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400">Nessun codice sconto. Creane uno!</p>
            </div>
          ) : (
            codes.map((code) => (
              <div key={code.id} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
                {/* Code Header */}
                <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${code.active ? "bg-green-500/20" : "bg-zinc-800"}`}>
                      <Ticket className={`w-6 h-6 ${code.active ? "text-green-500" : "text-zinc-500"}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-bold text-white">{code.code}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${code.active ? "bg-green-500/20 text-green-500" : "bg-red-500/20 text-red-500"}`}>
                          {code.active ? "Attivo" : "Disattivato"}
                        </span>
                      </div>
                      <p className="text-zinc-400 text-sm">
                        {code.discount_type === "free_shipping" ? "Spedizione gratuita" : code.discount_type === "percentage" ? `${code.discount_value}% di sconto` : `€${code.discount_value} di sconto`}
                        {code.min_order > 0 && ` • Min. €${code.min_order}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    {/* Stats */}
                    <div className="flex gap-4 text-center">
                      <div>
                        <p className="text-zinc-500 text-xs">Utilizzi</p>
                        <p className="text-white font-bold">{code.uses_count || 0}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs">Sconti Dati</p>
                        <p className="text-green-500 font-bold">€{(code.stats?.total_discount_given || 0).toFixed(2)}</p>
                      </div>
                      {code.affiliate_name && (
                        <div>
                          <p className="text-zinc-500 text-xs">Da Pagare</p>
                          <p className="text-yellow-500 font-bold">€{(code.stats?.unpaid_commission || 0).toFixed(2)}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpandedCode(expandedCode === code.id ? null : code.id)}
                        className="bg-zinc-800 hover:bg-zinc-700 text-white p-2 rounded-lg transition-colors"
                        title="Dettagli"
                      >
                        {expandedCode === code.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => startEdit(code)}
                        className="bg-zinc-800 hover:bg-zinc-700 text-white p-2 rounded-lg transition-colors"
                        title="Modifica"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(code.id)}
                        className="bg-red-500/20 hover:bg-red-500/30 text-red-500 p-2 rounded-lg transition-colors"
                        title="Elimina"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedCode === code.id && (
                  <div className="border-t border-zinc-800 p-4 space-y-4">
                    {/* Affiliate Info */}
                    {code.affiliate_name ? (
                      <div className="bg-zinc-800/50 rounded-lg p-4">
                        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-500" />
                          Affiliato / Titolare
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-zinc-500 text-xs">Nome</p>
                            <p className="text-white">{code.affiliate_name}</p>
                          </div>
                          {code.affiliate_email && (
                            <div>
                              <p className="text-zinc-500 text-xs">Email</p>
                              <p className="text-white flex items-center gap-1">
                                <Mail className="w-4 h-4" />
                                {code.affiliate_email}
                              </p>
                            </div>
                          )}
                          {code.affiliate_phone && (
                            <div>
                              <p className="text-zinc-500 text-xs">Telefono</p>
                              <p className="text-white flex items-center gap-1">
                                <Phone className="w-4 h-4" />
                                {code.affiliate_phone}
                              </p>
                            </div>
                          )}
                          <div>
                            <p className="text-zinc-500 text-xs">Commissione</p>
                            <p className="text-green-500 font-bold">{code.commission_percentage || 10}%</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-zinc-800/30 rounded-lg p-4 text-center">
                        <p className="text-zinc-500">Nessun affiliato associato a questo codice</p>
                        <button
                          onClick={() => startEdit(code)}
                          className="text-green-500 hover:underline text-sm mt-2"
                        >
                          + Aggiungi affiliato
                        </button>
                      </div>
                    )}

                    {/* Monthly Stats */}
                    {code.stats?.monthly && Object.keys(code.stats.monthly).length > 0 && (
                      <div>
                        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-green-500" />
                          Storico Mensile
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="text-zinc-500 text-sm border-b border-zinc-800">
                                <th className="text-left py-2 px-3">Mese</th>
                                <th className="text-center py-2 px-3">Ordini</th>
                                <th className="text-right py-2 px-3">Sconto Dato</th>
                                <th className="text-right py-2 px-3">Commissione</th>
                                <th className="text-center py-2 px-3">Stato</th>
                                <th className="text-center py-2 px-3">Azione</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(code.stats.monthly)
                                .sort((a, b) => b[0].localeCompare(a[0]))
                                .map(([month, stats]) => (
                                  <tr key={month} className="border-b border-zinc-800/50">
                                    <td className="py-2 px-3 text-white">
                                      {new Date(month + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
                                    </td>
                                    <td className="py-2 px-3 text-center text-white">{stats.orders}</td>
                                    <td className="py-2 px-3 text-right text-green-500">€{stats.discount.toFixed(2)}</td>
                                    <td className="py-2 px-3 text-right text-yellow-500 font-semibold">€{stats.commission.toFixed(2)}</td>
                                    <td className="py-2 px-3 text-center">
                                      {stats.paid ? (
                                        <button
                                          onClick={() => handleUnmarkPaid(code, month)}
                                          className="bg-green-500/20 text-green-500 text-xs px-2 py-1 rounded-full hover:bg-green-500/30 cursor-pointer"
                                          title="Clicca per annullare pagamento"
                                        >
                                          Pagato
                                        </button>
                                      ) : (
                                        <span className="bg-yellow-500/20 text-yellow-500 text-xs px-2 py-1 rounded-full">Da pagare</span>
                                      )}
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                      {!stats.paid && (
                                        <button
                                          onClick={() => openPayModal(code, month, stats.commission)}
                                          className="bg-green-500 text-black text-xs px-3 py-1 rounded-lg font-semibold hover:bg-green-600"
                                        >
                                          Segna Pagato
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* No usage yet */}
                    {(!code.stats?.monthly || Object.keys(code.stats.monthly).length === 0) && (
                      <div className="bg-zinc-800/30 rounded-lg p-4 text-center">
                        <p className="text-zinc-500">Questo codice non è ancora stato utilizzato</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-2xl w-full my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingCode ? "Modifica Codice" : "Nuovo Codice Sconto"}
              </h2>
              <button onClick={() => { setShowForm(false); setEditingCode(null); }} className="text-zinc-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Dati Codice */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-semibold mb-2">Codice *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="Es. ESTATE2024"
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white uppercase"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Descrizione</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Es. Sconto estivo 2024"
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-white font-semibold mb-2">Tipo Sconto</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                  >
                    <option value="percentage">Percentuale %</option>
                    <option value="fixed">Fisso €</option>
                    <option value="free_shipping">Spedizione gratuita</option>
                  </select>
                </div>
                {formData.discount_type !== "free_shipping" && (
                <div>
                  <label className="block text-white font-semibold mb-2">
                    Valore {formData.discount_type === "percentage" ? "%" : "€"} *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                    placeholder={formData.discount_type === "percentage" ? "10" : "5"}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                    required
                  />
                </div>
                )}
                <div>
                  <label className="block text-white font-semibold mb-2">Ordine Minimo €</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.min_order}
                    onChange={(e) => setFormData({...formData, min_order: e.target.value})}
                    placeholder="30"
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-white font-semibold mb-2">Utilizzi Max</label>
                  <input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                    placeholder="Illimitato"
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Valido Da</label>
                  <input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Valido Fino</label>
                  <input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                  />
                </div>
              </div>

              {/* Sezione Affiliato */}
              <div className="border-t border-zinc-800 pt-6">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Dati Affiliato / Titolare (opzionale)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-semibold mb-2">Nome Affiliato</label>
                    <input
                      type="text"
                      value={formData.affiliate_name}
                      onChange={(e) => setFormData({...formData, affiliate_name: e.target.value})}
                      placeholder="Es. Mario Rossi"
                      className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2">% Commissione</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={formData.commission_percentage}
                      onChange={(e) => setFormData({...formData, commission_percentage: e.target.value})}
                      placeholder="0"
                      className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2">Email Affiliato</label>
                    <input
                      type="email"
                      value={formData.affiliate_email}
                      onChange={(e) => setFormData({...formData, affiliate_email: e.target.value})}
                      placeholder="email@esempio.it"
                      className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2">Telefono Affiliato</label>
                    <input
                      type="tel"
                      value={formData.affiliate_phone}
                      onChange={(e) => setFormData({...formData, affiliate_phone: e.target.value})}
                      placeholder="333 1234567"
                      className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="active" className="text-white">Codice attivo</label>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingCode(null); }}
                  className="flex-1 bg-zinc-800 text-white py-3 rounded-lg font-semibold hover:bg-zinc-700"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-green-500 text-black py-3 rounded-lg font-bold hover:bg-green-600"
                >
                  {editingCode ? "Salva Modifiche" : "Crea Codice"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-green-500" />
                Registra Pagamento
              </h2>
              <button onClick={() => setShowPayModal(false)} className="text-zinc-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="bg-zinc-800 rounded-lg p-3 mb-4">
              <p className="text-white font-semibold">{paymentData.codeName}</p>
              <p className="text-zinc-400 text-sm">{paymentData.affiliateName}</p>
              <p className="text-zinc-400 text-sm">
                {paymentData.month && new Date(paymentData.month + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-white font-semibold mb-2">Importo €</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value) || 0})}
                  className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white"
                />
              </div>
              <div>
                <label className="block text-white font-semibold mb-2">Note (opzionale)</label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                  placeholder="Es. Bonifico effettuato"
                  rows={2}
                  className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPayModal(false)}
                  className="flex-1 bg-zinc-800 text-white py-3 rounded-lg font-semibold hover:bg-zinc-700"
                >
                  Annulla
                </button>
                <button
                  onClick={handleMarkPaid}
                  className="flex-1 bg-green-500 text-black py-3 rounded-lg font-bold hover:bg-green-600"
                >
                  Conferma Pagamento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDiscountCodes;
